import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import LabelWithInfo from './shared/LabelWithInfo';
import { departmentsApi, metricsApi } from '../services/api';
import { Department, Metric } from '../types';
import { HubThemeScope } from './clients/hub/designSystem';

interface MetricSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  metricId?: number;
  onOpenTarget?: (metricId: number) => void;
  onSaved?: () => void;
}

const MetricSettingsDialog: React.FC<MetricSettingsDialogProps> = ({
  open,
  onClose,
  metricId: initialMetricId,
  onOpenTarget: _onOpenTarget,
  onSaved,
}) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [metricId, setMetricId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [unit, setUnit] = useState('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [metricKind, setMetricKind] = useState<'kpi' | 'indicator'>('kpi');
  const [sourceType, setSourceType] = useState<'manual' | 'api'>('api');

  useEffect(() => {
    if (!open) return;
    const loadData = async () => {
      try {
        const [departmentsData, metricsData] = await Promise.all([
          departmentsApi.getAll(),
          metricsApi.getAll(),
        ]);
        setDepartments(departmentsData);
        setMetrics(metricsData);
      } catch (error) {
        console.error('Failed to load metric settings data:', error);
      }
    };
    loadData();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMetricId('');
      return;
    }
    if (initialMetricId) {
      setMetricId(initialMetricId);
    }
  }, [open, initialMetricId]);

  useEffect(() => {
    const loadMetric = async () => {
      if (!metricId) return;
      try {
        const metric: any = await metricsApi.getById(metricId as number);
        setName(metric.name);
        setDescription(metric.description || '');
        setUnit(metric.unit || '');
        setDepartmentId(metric.department_id || '');
        setMetricKind(metric.metric_type || 'kpi');
        setSourceType(metric.source_type || 'manual');
      } catch (error) {
        console.error('Failed to load metric settings:', error);
      }
    };
    loadMetric();
  }, [metricId]);

  const handleSave = async () => {
    try {
      if (metricId) {
        const payload: any = {
          name,
          description: description || null,
          unit: unit || null,
          department_id: departmentId !== '' ? (departmentId as number) : null,
          metric_type: metricKind,
          source_type: sourceType,
        };
        await metricsApi.update(metricId as number, payload);
      }
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Failed to save metric settings:', error);
      alert('Failed to save metric settings.');
    }
  };

  const renderLabel = (label: string, tooltip: string) => (
    <LabelWithInfo label={label} tooltip={tooltip} />
  );

  return (
    <HubThemeScope>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Metric Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Configure what this metric is, where it belongs, and how it appears on the scorecard.
            Targets are managed in the Settings &rarr; Quarterly Targets tab.
          </Typography>

          {/* Metric selector */}
          <FormControl fullWidth>
            <InputLabel>{renderLabel('Metric', 'Pick the metric to configure.')}</InputLabel>
            <Select
              value={metricId}
              label="Metric"
              onChange={(e) => setMetricId(e.target.value as number | '')}
            >
              <MenuItem value="">Select metric</MenuItem>
              {metrics.map((metric) => (
                <MenuItem key={metric.id} value={metric.id}>
                  {metric.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Department */}
          <FormControl fullWidth>
            <InputLabel>{renderLabel('Department', 'Assign where this metric belongs.')}</InputLabel>
            <Select
              value={departmentId}
              label="Department"
              onChange={(e) => setDepartmentId(e.target.value as number | '')}
            >
              <MenuItem value="">No department</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Name + Unit */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label={renderLabel('Metric Name', 'Display name shown on the scorecard.')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label={renderLabel('Unit', 'Optional unit (e.g. $, %, tickets, hours).')}
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              fullWidth
              placeholder="e.g. $, %, tickets"
            />
          </Box>

          {/* Metric Kind: KPI vs Indicator */}
          <FormControl fullWidth>
            <InputLabel>
              {renderLabel(
                'Metric Type',
                'KPI = full target tracking with pace. Indicator = simple totals without target/pace.'
              )}
            </InputLabel>
            <Select
              value={metricKind}
              label="Metric Type"
              onChange={(e) => setMetricKind(e.target.value as 'kpi' | 'indicator')}
            >
              <MenuItem value="kpi">KPI</MenuItem>
              <MenuItem value="indicator">Indicator</MenuItem>
            </Select>
          </FormControl>

          {/* Source badge (read-only info) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Source:</Typography>
            <Chip
              label={sourceType === 'api' ? 'API Sync' : 'Manual'}
              size="small"
              sx={{
                height: 22,
                fontSize: '12.5px',
                fontWeight: 600,
                backgroundColor: sourceType === 'api' ? 'var(--indigo-soft)' : 'rgba(var(--overlay-rgb),0.08)',
                color: sourceType === 'api' ? 'var(--indigo-on-soft)' : 'text.secondary',
              }}
            />
          </Box>

          {/* Description */}
          <TextField
            label={renderLabel('Description', 'What this metric tracks.')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
    </HubThemeScope>
  );
};

export default MetricSettingsDialog;
